import React, {
  useState, useRef, useContext, useEffect,
} from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import { Button } from '@/shared/components/Button';
import { QRCodeSVG } from 'qrcode.react';
import { AuthContext } from '../../../context/AuthContext';

const VoucherPDF = ({ bookingID }) => {
  const [voucherData, setVoucherData] = useState([]);
  const [paxDetails, setPaxDetails] = useState([]);
  const [logo, setLogo] = useState(null); // State to store logo
  const [adImage, setAdImage] = useState(null); // State to store ad image
  const { apiGlobal } = useContext(AuthContext);

  const contentRefs = useRef([]);

  const fetchVoucherData = async () => {
    try {
      const url = await fetch(`${apiGlobal}/agent/voucher_data.php?booking_id=${bookingID}`);
      const response = await url.json();
      if (response.status && response.message === 'Success' && response.data) {
        setVoucherData(response.data);
        setPaxDetails(response.data[0].pax_details.flat());
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchVoucherData();
  }, [bookingID]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Pre-load the image to check dimensions
        const img = new Image();
        img.onload = () => {
          setAdImage(reader.result);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const getMealType = (mealOffered) => {
    switch (mealOffered) {
      case '1':
        return 'breakfast';
      case '2':
        return 'snacks';
      case '3':
        return 'lunch';
      case '4':
        return 'dinner';
      default:
        return null; // Return null if no match
    }
  };
     
  const handleGeneratePdf = () => {
    // eslint-disable-next-line
    const pdf = new jsPDF('l', 'mm', 'a4');
    const imgWidth = 380; // Image width for the PDF
    let yOffset = 1; // Starting Y position
    let count = 0; // Counter to track the vouchers per page

    const addVoucherToPdf = (index, canvas) => {
      // Compress and reduce image quality before adding it to the PDF
      const imgData = canvas.toDataURL('image/jpeg', 1); // Reduce quality (50% quality)

      // Reduce the image size if needed (scale it to fit)
      const scaleHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'JPEG', 0, yOffset, imgWidth, scaleHeight); // Add image in JPEG format
      yOffset += scaleHeight;

      count += 1;

      if (count % 2 === 0) {
        pdf.addPage(); // Create a new page after every 2 vouchers
        yOffset = 10; // Reset for new page
      }
    };

    const processVouchers = () => {
      const voucherPromises = paxDetails.map((data, index) => {
        const input = contentRefs.current[index];

        return new Promise((resolve, reject) => {
          if (input && input.current) {
            // Capture the content as a canvas
            html2canvas(input.current, { scale: 1.5, useCORS: true }) // Reduce scale for lower resolution
              .then((canvas) => {
                addVoucherToPdf(index, canvas);
                resolve();
              })
              .catch((error) => {
                console.error(`Error capturing voucher for index ${index}:`, error);
                reject(error);
              });
          } else {
            reject(new Error(`Content not available for index ${index}`));
          }
        });
      });

      // Wait for all vouchers to be processed before saving the PDF
      Promise.all(voucherPromises)
        .then(() => {
          pdf.save('all_vouchers.pdf'); // Download the PDF after all vouchers are added
        })
        .catch((error) => {
          console.error('Error generating PDF:', error);
        });
    };

    processVouchers(); // Start processing the vouchers
  };

  const formatDate = (dateString) => {
    if (!dateString) return ''; // Handle empty values
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  return (
    <div>
      {/* Input Controls */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '20px' }}>
        <div>
          <label htmlFor="logo-upload" style={{ display: 'block', marginBottom: '5px' }}>Upload Logo:</label>
          <input
            id="logo-upload"
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
          />
        </div>
        <div>
          <label htmlFor="ad-upload" style={{ display: 'block', marginBottom: '5px' }}>Upload Advertisement:</label>
          <input
            id="ad-upload"
            type="file"
            accept="image/*"
            onChange={handleAdImageChange}
          />
          <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
            Recommended ad dimensions: 300x600px (vertical)
          </div>
        </div>
      </div>

      {paxDetails.map((pax, index) => {
        contentRefs.current[index] = contentRefs.current[index] || React.createRef();

        return (
          <div 
            key={voucherData[0]?.trip_id}
            ref={contentRefs.current[index]}
          >
            <div
              style={{
                width: '9in', // Fixed width to accommodate ad
                display: 'flex',
                alignItems: 'stretch', // Makes all children the same height
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                border: '2px dotted black',
              }}
            >
              {/* Main Voucher Content - Fixed Width */}
              <div
                style={{
                  width: '7in', // Fixed width
                  flexShrink: 0, // Prevents shrinking
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px dotted yellow',
                  paddingBottom: '16px',
                  backgroundImage: 'url(https://i.ibb.co/cS4jWYts/BG-Blue.jpg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  minHeight: '50mm',
                  padding: '20px',
                }}
              >
                {/* Logo Section */}
                {logo && (
                  <div style={{ width: '120px', height: '80px', marginRight: '20px' }}>
                    <img
                      src={logo}
                      alt="Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                )}

                <div>
                  <h2 style={{
                    fontSize: '24px', fontWeight: 700, color: 'yellow', marginTop: '20px',
                  }}
                  >
                    {(voucherData[0]?.trip_uid || '') + (bookingID || '') + (pax?.id || '')}
                  </h2>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
                    <tbody>
                      <tr>
                        <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Voucher For </td>
                        <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
                        <td style={{
                          width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow',
                        }}
                        >
                          {pax.pax_name}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Meal</td>
                        <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
                        <td style={{
                          width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow',
                        }}
                        >
                          {voucherData[0]?.meal_name}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Restaurant</td>
                        <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
                        <td style={{
                          width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow',
                        }}
                        >
                          {voucherData[0]?.restaurant_details?.name}, {voucherData[0]?.restaurant_details?.address1},
                          {voucherData[0]?.restaurant_details?.address2}, {voucherData[0]?.restaurant_details?.area},
                          {voucherData[0]?.restaurant_details?.city}, {voucherData[0]?.restaurant_details?.country} -
                          {voucherData[0]?.restaurant_details?.pincode}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Date & Time</td>
                        <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
                        <td style={{
                          width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow',
                        }}
                        >
                          {formatDate(voucherData[0]?.booking_date)}{' '} {getMealType(voucherData[0]?.meal_offered)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* QR Code Section */}
                <div
                  className="qrholder"
                  style={{
                    width: '100px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                > 
                  <QRCodeSVG
                    value={`
                      Voucher Id:${(voucherData[0]?.trip_uid || '') + (bookingID || '') + (pax?.id || '')}
                      Pasanger Name:${pax.pax_name}
                      `}
                    size={100}
                    link="www.google.com"
                    style={{ width: '100px', height: '100px' }}
                  />
                </div>
              </div>
              
              {/* Right Side Advertisement - Fixed Width */}
              <div style={{ 
                width: '2in', // Fixed width
                flexShrink: 0, // Prevents shrinking
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'forestgreen',
                padding: '5px',
                borderLeft: '1px dotted #ccc',
                overflow: 'hidden', // Prevents content from spilling out
              }}
              >
                {/* Top Ad Section - Takes 75% of height */}
                <div style={{ 
                  height: '75%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '10px',
                  overflow: 'hidden', // Important: Prevents image overflow
                }}
                >
                  {adImage ? (
                    <div style={{ 
                      width: '100%',
                      height: '200px', // Fixed height container
                      overflow: 'hidden', // Important: Prevents image overflow
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    >
                      <img 
                        src={adImage} 
                        alt="Advertisement" 
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain', // Contains the image within bounds
                        }} 
                      />
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '10px', 
                      textAlign: 'center',
                      color: 'white',
                    }}
                    >
                      Upload an ad image
                    </div>
                  )}
                </div>
                
                {/* Bottom Company Info Section - Takes 25% of height */}
                <div style={{ 
                  height: '25%',
                  width: '100%',
                  borderTop: '1px dotted #ccc',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: 'white',
                }}
                >
                  <div>For advertisement inquiries:</div>
                  <div>marketing@yourcompany.com</div>
                  <div>+1 123-456-7890</div>
                </div>
              </div>
            </div>
           
          </div>
        );
      })}

      {/* Generate PDF Button */}
      <Button
        variant="outline-primary"
        title="Download PDF"
        onClick={handleGeneratePdf}
        style={{ marginTop: '20px' }}
      >
        Generate PDF
      </Button>
    </div>
  );
};

VoucherPDF.propTypes = {
  bookingID: PropTypes.string.isRequired,
};

export default VoucherPDF;

// import React, {
//  useState, useRef, useContext, useEffect, 
// } from 'react';
// import html2canvas from 'html2canvas';
// import { jsPDF } from 'jspdf';
// import PropTypes from 'prop-types';
// import { Button } from '@/shared/components/Button';
// import { AuthContext } from '../../../context/AuthContext';

// const VoucherPDF = ({ bookingID }) => {
//   const [voucherData, setVoucherData] = useState([]);
//   const [paxDetails, setPaxDetails] = useState([]);
//   const { apiGlobal } = useContext(AuthContext);

//   const contentRefs = useRef([]);

//   const fetchVoucherData = async () => {
//     try {
//       const url = await fetch(`${apiGlobal}/voucher_data.php?booking_id=${bookingID}`);
//       const response = await url.json();
//       if (response.status && response.message === 'Success' && response.data) {
//         setVoucherData(response.data);
//         setPaxDetails(response.data[0].pax_details.flat());
//       }
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   useEffect(() => {
//     fetchVoucherData();
//   }, [bookingID]);



//  // this handler function for apperaing all voucher in simple page 
//   // const handleGeneratePdf = () => {
//   //   const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape layout (for wider vouchers)
//   //   const imgWidth = 350; // Increased width of the voucher
//   //   const imgHeight = 150; // Increased height of the voucher
//   //   let yOffset = 10; // Starting Y offset for the first voucher
//   //   const xOffset = -5; // Corrected starting X offset for the left side of the page
  
//   //   const addVoucherToPdf = (index, canvas) => {
//   //     const imgData = canvas.toDataURL('image/png');
//   //     const scaleHeight = (canvas.height * imgWidth) / canvas.width;
  
//   //     // Add the voucher to the current position (left-aligned)
//   //     pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, scaleHeight);
  
//   //     // Update yOffset for the next voucher (no gap)
//   //     yOffset += scaleHeight;
  
//   //     // Check if the current voucher overflows the bottom of the page
//   //     if (yOffset > 270) { // Near the bottom of the page
//   //       yOffset = 10; // Reset the yOffset
//   //       pdf.addPage(); // Add a new page if needed
//   //     }
//   //   };
  
//   //   const processVouchers = () => {
//   //     const voucherPromises = paxDetails.map((data, index) => {
//   //       const input = contentRefs.current[index];
  
//   //       return new Promise((resolve, reject) => {
//   //         if (input && input.current) {
//   //           html2canvas(input.current, { scale: 6, useCORS: true })
//   //             .then((canvas) => {
//   //               addVoucherToPdf(index, canvas);
//   //               resolve();
//   //             })
//   //             .catch((error) => {
//   //               console.error(`Error capturing voucher for index ${index}:`, error);
//   //               reject(error);
//   //             });
//   //         } else {
//   //           reject(`Content not available for index ${index}`);
//   //         }
//   //       });
//   //     });
  
//   //     Promise.all(voucherPromises)
//   //       .then(() => {
//   //         pdf.save('all_vouchers.pdf');
//   //       })
//   //       .catch((error) => {
//   //         console.error('Error generating PDF:', error);
//   //       });
//   //   };
  
//   //   processVouchers();
//   // };

//   const handleGeneratePdf = () => {
//      // eslint-disable-next-line new-cap
//     const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape layout (for wider vouchers)
//     const imgWidth = 500; // Width of the voucher
//     const imgHeight = 120; // Height of the voucher
  
//     const addVoucherToPdf = (index, canvas) => {
//       const imgData = canvas.toDataURL('image/png');
//       const scaleHeight = (canvas.height * imgWidth) / canvas.width;
  
//       // Add the voucher to the current page
//       pdf.addImage(imgData, 'PNG', 0, 1, imgWidth, scaleHeight);
  
//       // Add a new page after each voucher
//       pdf.addPage();
//     };
  
//     const processVouchers = () => {
//       const voucherPromises = paxDetails.map((data, index) => {
//         const input = contentRefs.current[index];
  
//         return new Promise((resolve, reject) => {
//           if (input && input.current) {
//             html2canvas(input.current, { scale: 6, useCORS: true })
//               .then((canvas) => {
//                 addVoucherToPdf(index, canvas);
//                 resolve();
//               })
//               .catch((error) => {
//                 console.error(`Error capturing voucher for index ${index}:`, error);
//                 reject(error);
//               });
//           } else {
//             reject(new Error(`Content not available for index ${index}`));
//           }
//         });
//       });
  
//       Promise.all(voucherPromises)
//         .then(() => {
//           pdf.save('all_vouchers.pdf');
//         })
//         .catch((error) => {
//           console.error('Error generating PDF:', error);
//         });
//     };
  
//     processVouchers();
//   };


//   const formatDate = (dateString) => {
//     if (!dateString) return ''; // Handle empty values
//     const [year, month, day] = dateString.split('-');
//     return `${day}-${month}-${year}`;
//   };

//   const getMealType = (mealOffered) => {
//     switch (mealOffered) {
//       case '1':
//         return 'breakfast';
//       case '2':
//         return 'snacks';
//       case '3':
//         return 'lunch';
//       case '4':
//         return 'dinner';
//       default:
//         return null; // Return null if no match
//     }
//   };

  
  
  

//   return (
//     <div>
//       {paxDetails.map((pax, index) => {
//         contentRefs.current[index] = contentRefs.current[index] || React.createRef();

//         return (
//           <div key={voucherData[0]?.trip_uid} ref={contentRefs.current[index]}>
//             <div
//               style={{
//                 width: '7in',
//                 display: 'flex',
//                 borderBottom: '1px dotted yellow',
//                 fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
//                 paddingBottom: '16px',
//                 margin: '0',
//                 pageBreakInside: 'avoid',
//                 backgroundImage: 'url(https://i.ibb.co/cS4jWYts/BG-Blue.jpg)',
//                 backgroundSize: 'cover',
//                 backgroundPosition: 'center',
//                 backgroundRepeat: 'no-repeat',
//                 minHeight: '50mm', // Increased height for better spacing
//               }}
//             >
//               <div style={{
//  fontStyle: 'normal',
// fontWeight: 700, 
//  fontSize: '16px',
// color: '#03041d',
// // display: 'flex',
// // alignItems: 'center',
// marginTop: '52px',
// // marginLeft: '37px',
// width: '3in', 
// }}
//               >
//                 <div><br /></div>
//               </div>
//               <div>
//                 <h2 style={{
//  fontSize: '24px', fontWeight: 700, color: 'yellow', marginTop: '20px', 
// }}
//                 >
//                   {voucherData[0]?.trip_uid}
//                 </h2>
//                 <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
//                   <tr>
//                     <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Voucher For </td>
//                     <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
//                     <td style={{
//  width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow', 
// }}
//                     >
//                       {pax.pax_name}
//                     </td>
//                   </tr>
//                   <tr>
//                     <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Meal</td>
//                     <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
//                     <td style={{
//  width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow', 
// }}
//                     >
//                       {voucherData[0]?.meal_name}
//                     </td>
//                   </tr>
//                   <tr>
//                     <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Restaurant</td>
//                     <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
//                     <td style={{
//  width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow', 
// }}
//                     >
//                       {voucherData[0]?.restaurant_details?.name}, {voucherData[0]?.restaurant_details?.address1},
//                       {voucherData[0]?.restaurant_details?.address2}, {voucherData[0]?.restaurant_details?.area},
//                       {voucherData[0]?.restaurant_details?.city}, {voucherData[0]?.restaurant_details?.country} -
//                       {voucherData[0]?.restaurant_details?.pincode}
//                     </td>
//                   </tr>
//                   <tr>
//                     <td style={{ width: '25%', verticalAlign: 'top', color: 'white' }}>Date & Time</td>
//                     <td style={{ width: '5%', verticalAlign: 'top', color: 'white' }}>:</td>
//                     <td style={{
//  width: '70%', verticalAlign: 'top', textAlign: 'left', color: 'yellow', 
// }}
//                     >
//                       {formatDate(voucherData[0]?.booking_date)}{' '} {getMealType(voucherData[0]?.meal_offered)}
//                     </td>
//                   </tr>
//                 </table>
//               </div>
//               <div className="qrholder" style={{ width: '1in', display: 'flex', alignItems: 'center' }}>
//                 <img
//                   style={{ width: '100%' }} 
//                   src={`data:image/png;base64,${voucherData[0]?.base64_qr_code}`}
//                   alt="QR Code"
//                 />
//               </div>
//             </div>
//           </div>
//         );
//       })}
//       <Button 
//         variant="outline-primary" 
//         title="Download PDF"
//         onClick={handleGeneratePdf}
//         style={{ marginTop: '20px' }}
//       >
//         Generate PDF
//       </Button>
      
//     </div>
//   );
// };

// VoucherPDF.propTypes = {
//   bookingID: PropTypes.string.isRequired,
// };

// export default VoucherPDF;
